import { type ReactNode } from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import { FEATURES } from '../../lib/feature-flags';
import { Button } from '../ui/Button';

interface SectionErrorBoundaryProps {
  /** Name of the section (e.g., "WorkOrders", "Clients") */
  sectionName: string;
  /** Child components to wrap */
  children: ReactNode;
}

/**
 * SectionErrorBoundary - Pre-configured error boundary for app sections
 *
 * Wraps major app sections with isolated error boundaries.
 * If an error occurs in one section, other sections remain functional.
 *
 * Features:
 * - Section-specific error messages
 * - Isolated error recovery
 * - Error tracking with section context
 * - Feature flag support for instant rollback
 *
 * @example
 * <SectionErrorBoundary sectionName="WorkOrders">
 *   <WorkordersCommandCenter />
 * </SectionErrorBoundary>
 */
export function SectionErrorBoundary({ sectionName, children }: SectionErrorBoundaryProps) {
  // Feature flag: disable section boundaries if needed (instant rollback)
  if (!FEATURES.USE_SECTION_ERROR_BOUNDARIES) {
    return <>{children}</>;
  }

  return (
    <ErrorBoundary
      config={{
        name: sectionName,
        title: `${sectionName} Error`,
        description: `An error occurred in the ${sectionName} section. Other parts of the app are still working.`,
        showResetButton: true,
        resetButtonLabel: 'Try Again',
        fallback: (error, reset) => (
          <div className="flex-1 flex items-center justify-center p-8" role="alert">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center mb-4 mx-auto">
                <span className="text-3xl">⚠️</span>
              </div>
              <h2 className="font-display text-xl font-semibold text-forest-900 mb-2">
                {sectionName} Error
              </h2>
              <p className="text-sage-600 mb-6">
                An error occurred in the {sectionName} section. Other parts of the app are still working.
              </p>
              {import.meta.env.DEV && (
                <details className="mb-6 text-left">
                  <summary className="cursor-pointer text-sm text-sage-500 mb-2">
                    Error Details (Dev Mode)
                  </summary>
                  <pre className="text-xs text-red-600 bg-red-50 p-2 rounded overflow-auto">
                    {error.message}
                    {'\n\n'}
                    {error.stack}
                  </pre>
                </details>
              )}
              <div className="flex gap-3 justify-center">
                <Button variant="secondary" onClick={() => window.location.href = '/'}>
                  Go Home
                </Button>
                <Button variant="primary" onClick={reset}>
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        ),
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
